"use client";

import { useState, useCallback, useEffect, Suspense } from "react";
import Image from "next/image";
import { useSearchParams, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Sparkles,
  Loader2,
  RotateCcw,
  Star,
  Shuffle,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ── Spread Definitions ────────────────────────────────────────────────────────

interface SpreadDefinition {
  id: string;
  label: string;
  shortLabel: string;
  cardCount: number;
  description: string;
  positions: string[];
  layoutClass: string; // hint for grid display
}

const SPREADS: SpreadDefinition[] = [
  {
    id: "three_card",
    label: "3 Card Basic Question",
    shortLabel: "3 Card",
    cardCount: 3,
    description: "A quick, direct spread for everyday questions — past, present, future",
    positions: ["Past", "Present", "Future"],
    layoutClass: "grid-cols-3",
  },
  {
    id: "five_card",
    label: "5 Card Complex Question",
    shortLabel: "5 Card",
    cardCount: 5,
    description: "Deeper insight into a challenge — situation, obstacles, advice, outcome",
    positions: ["Situation", "Obstacles", "Hidden Factors", "Advice", "Likely Outcome"],
    layoutClass: "grid-cols-5",
  },
  {
    id: "seven_forward",
    label: "7 Card 6-Month Review",
    shortLabel: "7 Card (6-Month)",
    cardCount: 7,
    description: "Monthly energies over the next 6 months plus a summary",
    positions: ["Month 1", "Month 2", "Month 3", "Month 4", "Month 5", "Month 6", "Overall Theme"],
    layoutClass: "grid-cols-4",
  },
  {
    id: "horseshoe",
    label: "7 Card Horseshoe Spread",
    shortLabel: "Horseshoe",
    cardCount: 7,
    description: "A major reading covering past patterns through to final outcome",
    positions: ["Past", "Present", "Hidden Influences", "Advice", "External Factors", "Hopes & Fears", "Outcome"],
    layoutClass: "grid-cols-4",
  },
  {
    id: "relationship",
    label: "10 Card Relationship Spread",
    shortLabel: "Relationship",
    cardCount: 10,
    description: "Full relationship dynamics — perceptions, needs, and path forward",
    positions: [
      "How You See Them",
      "How They See You",
      "The Relationship",
      "Your Needs",
      "Their Needs",
      "Common Ground",
      "Advice",
      "Relationship Strength",
      "The Challenge",
      "Outcome",
    ],
    layoutClass: "grid-cols-5",
  },
  {
    id: "celtic_cross",
    label: "10 Card Celtic Cross",
    shortLabel: "Celtic Cross",
    cardCount: 10,
    description: "The classic major tarot spread — comprehensive life overview",
    positions: [
      "Present Situation",
      "The Challenge",
      "Distant Past",
      "Recent Past",
      "Best Possible Outcome",
      "Near Future",
      "Your Feelings",
      "External Influences",
      "Hopes & Fears",
      "Final Outcome",
    ],
    layoutClass: "grid-cols-5",
  },
  {
    id: "astrological",
    label: "12 Card Astrological Spread",
    shortLabel: "Astrological",
    cardCount: 12,
    description: "One card per astrological house — a full-year cosmic reading",
    positions: [
      "1st House — Self",
      "2nd House — Resources",
      "3rd House — Communication",
      "4th House — Home",
      "5th House — Creativity",
      "6th House — Health",
      "7th House — Partnerships",
      "8th House — Transformation",
      "9th House — Philosophy",
      "10th House — Career",
      "11th House — Community",
      "12th House — Spirituality",
    ],
    layoutClass: "grid-cols-4",
  },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface TarotCardRecord {
  _id: string;
  name: string;
  image?: string;
  card_image?: string | any[]; // Support new S3 metadata array or legacy string
  keywords?: string;
  upright_meaning?: string;
  reversed_meaning?: string;
  status?: number;
}

interface DrawnCard extends TarotCardRecord {
  position: string;
  position_number: number;
  is_reversed: boolean;
}

interface ReadingResult {
  interpretation: string;
  card_interpretations: Record<string, string>;
}

// ── Form Schema ───────────────────────────────────────────────────────────────

const readingSchema = z.object({
  person_name: z.string().optional(),
  question: z.string().optional(),
});
type ReadingValues = z.infer<typeof readingSchema>;

// ── Card Display ──────────────────────────────────────────────────────────────

const CARD_IMAGE_BASE = "https://divineinfinitebeing.influxiq.com/assets/tarot-card-images/";
const CARD_BACK = "https://divineinfinitebeing.influxiq.com/assets/tarot-card-images/card-back.jpg";

function TarotCardTile({
  card,
  index,
  interpretation,
}: {
  card: DrawnCard;
  index: number;
  interpretation?: string;
}) {
  const [imgError, setImgError] = useState(false);

  // Determine the image source: Check new S3 card_image field first, then legacy image field
  let imgSrc = CARD_BACK;
  if (!imgError) {
    if (Array.isArray(card.card_image) && card.card_image[0]?.imgUrl) {
      imgSrc = card.card_image[0].imgUrl;
    } else if (typeof card.card_image === "string" && card.card_image) {
      imgSrc = `${CARD_IMAGE_BASE}${card.card_image}`;
    } else if (card.image) {
      imgSrc = `${CARD_IMAGE_BASE}${card.image}`;
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Card Image */}
      <div
        className={cn(
          "relative rounded-xl overflow-hidden shadow-lg border transition-all duration-300",
          card.is_reversed ? "rotate-180" : ""
        )}
        style={{
          width: 90,
          height: 150,
          borderColor: "hsl(240 20% 28%)",
          boxShadow: "0 4px 20px rgba(255,168,0,0.15)",
        }}
      >
        <Image
          src={imgSrc}
          alt={card.name}
          fill
          className="object-cover"
          onError={() => setImgError(true)}
        />
        {card.is_reversed && (
          <div
            className="absolute bottom-1 right-1 text-[9px] font-bold px-1 rounded"
            style={{ background: "rgba(233,74,73,0.85)", color: "#fff" }}
          >
            Rx
          </div>
        )}
      </div>

      {/* Position & Card Name */}
      <div className="text-center max-w-[100px]">
        <p
          className="text-[10px] font-semibold uppercase tracking-wide"
          style={{ color: "#ffa800" }}
        >
          {card.position}
        </p>
        <p className="text-xs font-medium text-foreground leading-tight mt-0.5">
          {card.name}
          {card.is_reversed && (
            <span className="text-red-400 ml-1">(Rx)</span>
          )}
        </p>
      </div>

      {/* AI Interpretation */}
      {interpretation && (
        <p
          className="text-[11px] text-foreground/75 leading-snug text-center max-w-[100px]"
        >
          {interpretation}
        </p>
      )}
    </div>
  );
}

// ── Main Content ──────────────────────────────────────────────────────────────

function TarotToolkitContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session } = useSession();

  const spreadParam = searchParams?.get("spread") ?? SPREADS[0].id;
  const selectedSpread = SPREADS.find((s) => s.id === spreadParam) ?? SPREADS[0];

  const [drawnCards, setDrawnCards] = useState<DrawnCard[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [result, setResult] = useState<ReadingResult | null>(null);

  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ?? "https://api.divineinfinitebeing.com/";

  const form = useForm<ReadingValues>({
    resolver: zodResolver(readingSchema),
    defaultValues: { person_name: "", question: "" },
  });

  // Fetch all tarot cards from the backend
  const { data: allCards, isLoading: loadingCards } = useQuery<TarotCardRecord[]>({
    queryKey: ["tarot-cards-all"],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}tarot-card/card-list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({
          condition: { status: 1 },
          sort: { type: "asc", field: "name" },
          limit: 200,
          skip: 0,
        }),
      });
      const data = await res.json() as { data?: TarotCardRecord[]; results?: TarotCardRecord[] };
      return data.data ?? data.results ?? [];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  // Reset drawn cards & result when spread changes
  useEffect(() => {
    setDrawnCards([]);
    setResult(null);
    form.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadParam]);

  const drawCards = useCallback(() => {
    const cards = allCards ?? [];
    if (cards.length < selectedSpread.cardCount) {
      toast.error(`Not enough cards in library (need ${selectedSpread.cardCount}, have ${cards.length})`);
      return;
    }

    setIsDrawing(true);
    setResult(null);

    // Fisher-Yates shuffle then take N
    const shuffled = [...cards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    const drawn: DrawnCard[] = shuffled
      .slice(0, selectedSpread.cardCount)
      .map((card, idx) => ({
        ...card,
        position: selectedSpread.positions[idx],
        position_number: idx + 1,
        is_reversed: Math.random() < 0.3, // 30% chance of reversed
      }));

    setDrawnCards(drawn);
    setIsDrawing(false);
    toast.success(`${selectedSpread.cardCount} cards drawn`);
  }, [allCards, selectedSpread]);

  async function onInterpret(values: ReadingValues) {
    if (!drawnCards.length) {
      toast.error("Draw cards first");
      return;
    }
    setIsInterpreting(true);
    setResult(null);

    try {
      const res = await fetch("/api/tarot/reading", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token ?? ""}`,
        },
        body: JSON.stringify({
          spread_type: selectedSpread.id,
          spread_label: selectedSpread.label,
          drawn_cards: drawnCards,
          person_name: values.person_name ?? "",
          question: values.question ?? "",
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `API error ${res.status}`);
      }

      const data = await res.json() as ReadingResult;
      setResult(data);

      if (!data.interpretation) {
        toast.info("Cards drawn. Configure ASTROLOGY_AI_LAMBDA_URL to enable AI interpretations.");
      } else {
        toast.success("Reading complete");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Interpretation failed");
    } finally {
      setIsInterpreting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div className="flex items-center gap-3">
        <div
          className="h-7 w-1 rounded-full shrink-0"
          style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }}
        />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Tarot Toolkit</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Interactive tarot readings with AI-powered interpretations
          </p>
        </div>
      </div>

      <div className="flex gap-5 items-start">
        {/* ── Left nav ──────────────────────────────────────────────── */}
        <nav
          className="hidden lg:flex flex-col gap-0.5 w-52 shrink-0 rounded-xl p-2"
          style={{ background: "hsl(240 28% 14%)", border: "1px solid hsl(240 20% 22%)" }}
        >
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
            Spread Type
          </p>
          {SPREADS.map((spread) => {
            const active = spread.id === selectedSpread.id;
            return (
              <button
                key={spread.id}
                type="button"
                onClick={() => router.push(`/admin/tarot/toolkit?spread=${spread.id}`)}
                className={cn(
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-all duration-150 text-left w-full",
                  active
                    ? "font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                )}
                style={
                  active
                    ? {
                        background:
                          "linear-gradient(to right, rgba(255,168,0,0.18), rgba(255,126,0,0.06))",
                        color: "#ffa800",
                        borderLeft: "2px solid #ffa800",
                      }
                    : undefined
                }
              >
                <Sparkles className="size-3.5 shrink-0" />
                <span className="truncate">{spread.label}</span>
                <span
                  className="ml-auto shrink-0 text-[9px] px-1 py-0.5 rounded font-medium"
                  style={{ background: "rgba(255,168,0,0.12)", color: "#ffa800" }}
                >
                  {spread.cardCount}
                </span>
              </button>
            );
          })}
        </nav>

        {/* ── Main ──────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Mobile spread tabs */}
          <div className="lg:hidden overflow-x-auto pb-1">
            <div className="flex gap-1.5 w-max">
              {SPREADS.map((spread) => {
                const active = spread.id === selectedSpread.id;
                return (
                  <button
                    key={spread.id}
                    type="button"
                    onClick={() => router.push(`/admin/tarot/toolkit?spread=${spread.id}`)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors",
                      active ? "font-semibold text-[#ffa800]" : "text-muted-foreground hover:text-foreground"
                    )}
                    style={
                      active
                        ? { background: "rgba(255,168,0,0.15)", border: "1px solid rgba(255,168,0,0.3)" }
                        : { background: "hsl(240 28% 14%)", border: "1px solid hsl(240 20% 22%)" }
                    }
                  >
                    {spread.shortLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Spread info + form */}
          <Card>
            <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                {selectedSpread.label}
                <Badge
                  variant="outline"
                  className="ml-1 text-xs"
                  style={{ color: "#ffa800", borderColor: "rgba(255,168,0,0.4)" }}
                >
                  {selectedSpread.cardCount} cards
                </Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{selectedSpread.description}</p>
            </CardHeader>
            <CardContent className="p-5 space-y-5">
              {/* Optional reading context */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    Name <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    {...form.register("person_name")}
                    placeholder="Who is this reading for?"
                    className="h-9"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    Question / Focus <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    {...form.register("question")}
                    placeholder="What is the focus of this reading?"
                    className="h-9"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Position labels preview */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Card Positions
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedSpread.positions.map((pos, i) => (
                    <span
                      key={pos}
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: "hsl(240 28% 17%)", color: "hsl(214 20% 65%)" }}
                    >
                      {i + 1}. {pos}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={drawCards}
                  disabled={isDrawing || loadingCards}
                  className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed btn-orange"
                >
                  {isDrawing || loadingCards ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      {loadingCards ? "Loading cards..." : "Drawing..."}
                    </>
                  ) : (
                    <>
                      <Shuffle className="size-3.5" />
                      {drawnCards.length ? "Redraw Cards" : "Draw Cards"}
                    </>
                  )}
                </button>

                {drawnCards.length > 0 && (
                  <button
                    type="button"
                    onClick={form.handleSubmit(onInterpret)}
                    disabled={isInterpreting}
                    className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: "linear-gradient(to bottom, #25aceb, #1a8fc2)",
                      color: "#fff",
                    }}
                  >
                    {isInterpreting ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        Interpreting...
                      </>
                    ) : (
                      <>
                        <Star className="size-3.5" />
                        Get AI Interpretation
                      </>
                    )}
                  </button>
                )}

                {(drawnCards.length > 0 || result) && (
                  <button
                    type="button"
                    onClick={() => {
                      setDrawnCards([]);
                      setResult(null);
                      form.reset();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    style={{ border: "1px solid hsl(240 20% 22%)" }}
                  >
                    <RotateCcw className="size-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Drawn Cards Display */}
          {drawnCards.length > 0 && (
            <Card>
              <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
                  {selectedSpread.label}
                  {isInterpreting && (
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground ml-auto" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <div
                  className={cn(
                    "grid gap-5 justify-items-center",
                    selectedSpread.cardCount <= 3
                      ? "grid-cols-3"
                      : selectedSpread.cardCount <= 5
                      ? "grid-cols-5"
                      : selectedSpread.cardCount <= 7
                      ? "grid-cols-4 sm:grid-cols-7"
                      : "grid-cols-4 sm:grid-cols-5"
                  )}
                >
                  {drawnCards.map((card, i) => (
                    <TarotCardTile
                      key={i}
                      card={card}
                      index={i}
                      interpretation={result?.card_interpretations?.[String(i + 1)]}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Overall AI Interpretation */}
          {result?.interpretation && (
            <Card>
              <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#25aceb" }} />
                  <ChevronRight className="size-3.5 text-[#ffa800]" />
                  Reading Interpretation
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5">
                <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line">
                  {result.interpretation}
                </p>
              </CardContent>
            </Card>
          )}

          {/* No cards yet — empty state */}
          {drawnCards.length === 0 && !isDrawing && (
            <div
              className="rounded-xl flex flex-col items-center justify-center py-16 gap-3"
              style={{ background: "hsl(240 28% 14%)", border: "1px dashed hsl(240 20% 26%)" }}
            >
              <div
                className="rounded-full p-4"
                style={{ background: "rgba(255,168,0,0.08)" }}
              >
                <Sparkles className="size-8 text-[#ffa800]/50" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                Select a spread and click <span className="text-foreground">Draw Cards</span> to begin
              </p>
              <p className="text-xs text-muted-foreground/60">
                {selectedSpread.cardCount} cards will be drawn for the {selectedSpread.label}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TarotToolkitPage() {
  return (
    <Suspense fallback={null}>
      <TarotToolkitContent />
    </Suspense>
  );
}
